/**
 * MessageInput Component - HYB Shop Chat
 * Auto-resize textarea with attachment and send functionality
 */

import { useRef, useEffect, useCallback } from 'react';
import { Send, Image as ImageIcon, RefreshCw } from 'lucide-react';
import { cn } from '../../../utils/responsive';

interface MessageInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onAttachClick?: () => void;
  isSending?: boolean;
  isUploading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

export function MessageInput({
  value,
  onChange,
  onSend,
  onAttachClick,
  isSending = false,
  isUploading = false,
  disabled = false,
  placeholder = 'พิมพ์ข้อความ...',
  className,
}: MessageInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to calculate proper scrollHeight
    textarea.style.height = 'auto';

    // Calculate new height (max 5 lines, ~120px)
    const lineHeight = 24;
    const maxLines = 5;
    const maxHeight = lineHeight * maxLines;
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);

    textarea.style.height = `${newHeight}px`;
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!disabled && !isSending && value.trim()) {
        onSend();
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  };

  const canSend = !disabled && !isSending && value.trim().length > 0;

  return (
    <div
      className={cn(
        'border-t border-stone-200 dark:border-gray-700',
        'bg-white dark:bg-gray-800',
        'px-4 lg:px-6 py-3 lg:py-4',
        className
      )}
    >
      <div className="flex items-end gap-2 lg:gap-3">
        {/* Textarea Container */}
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
            className={cn(
              'w-full resize-none',
              'rounded-xl border border-stone-300 dark:border-gray-600',
              'bg-stone-50 dark:bg-gray-900',
              'px-4 py-3',
              'text-sm text-stone-900 dark:text-gray-100',
              'placeholder:text-stone-400 dark:placeholder:text-gray-500',
              'transition-all duration-200',
              'focus:bg-white dark:focus:bg-gray-800',
              'focus:border-amber-400 dark:focus:border-amber-500',
              'focus:outline-none focus:ring-2 focus:ring-amber-400/20',
              'disabled:opacity-60 disabled:cursor-not-allowed',
              'scrollbar-thin scrollbar-thumb-stone-300 dark:scrollbar-thumb-gray-600'
            )}
            style={{ minHeight: '48px', maxHeight: '120px' }}
            aria-label={placeholder}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-2">
          {/* Attachment Button */}
          {onAttachClick && (
            <button
              type="button"
              onClick={onAttachClick}
              disabled={disabled || isUploading}
              className={cn(
                'flex h-11 w-11 items-center justify-center',
                'rounded-xl border border-stone-200 dark:border-gray-600',
                'bg-white dark:bg-gray-800',
                'text-stone-500 dark:text-gray-400',
                'transition-all duration-200',
                'hover:bg-stone-50 dark:hover:bg-gray-700',
                'hover:border-amber-400 dark:hover:border-amber-500',
                'hover:text-amber-600 dark:hover:text-amber-400',
                'active:scale-95',
                'focus:outline-none focus:ring-2 focus:ring-amber-400/50',
                'disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:border-stone-200'
              )}
              title="แนบรูปภาพ"
              aria-label="แนบรูปภาพ"
            >
              {isUploading ? (
                <RefreshCw className="h-5 w-5 animate-spin" />
              ) : (
                <ImageIcon className="h-5 w-5" />
              )}
            </button>
          )}

          {/* Send Button */}
          <button
            type="button"
            onClick={onSend}
            disabled={!canSend}
            className={cn(
              'flex h-11 w-11 items-center justify-center',
              'rounded-xl',
              'bg-gradient-to-br from-amber-400 via-amber-500 to-orange-500',
              'text-white',
              'shadow-sm shadow-amber-300/30 dark:shadow-none',
              'transition-all duration-200',
              'hover:shadow-lg hover:shadow-amber-300/40 dark:hover:shadow-amber-900/20',
              'hover:scale-105',
              'active:scale-95',
              'focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2',
              'dark:focus:ring-offset-gray-800',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'disabled:hover:scale-100 disabled:hover:shadow-sm'
            )}
            title="ส่งข้อความ"
            aria-label="ส่งข้อความ"
          >
            {isSending ? (
              <RefreshCw className="h-5 w-5 animate-spin" />
            ) : (
              <Send className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>

      {/* Helper text */}
      <p className="mt-2 text-[10px] text-stone-400 dark:text-gray-500" id="input-hint">
        กด Enter เพื่อส่ง หรือ Shift + Enter เพื่อขึ้นบรรทัดใหม่
      </p>
    </div>
  );
}

export default MessageInput;
